import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Card,
  Row,
  Col,
  Alert,
  Tooltip,
  Avatar
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  BookOutlined
} from '@ant-design/icons';
import { TeacherList, TeacherFilters, teacherAPI } from '../../services/teacherAPI';
import { UserCompany, Company } from '../../services/companyAPI';
import TeacherForm from './TeacherForm';

const { Search } = Input;

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

interface TeachersListProps {
  selectedCompany: SelectedCompany | null;
}

const TeachersList: React.FC<TeachersListProps> = ({ selectedCompany }) => {
  const [teachers, setTeachers] = useState<TeacherList[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<TeacherList | null>(null);
  const [filters, setFilters] = useState<TeacherFilters>({});

  const workPercentageLabels = {
    25: '25% (Part-time)',
    50: '50% (Half-time)',
    75: '75% (3/4 time)',
    100: '100% (Full-time)'
  };

  useEffect(() => {
    if (selectedCompany) {
      loadTeachers();
    }
  }, [selectedCompany, filters]);

  const loadTeachers = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const response = await teacherAPI.getTeachers({
        ...filters,
        company: selectedCompany.id
      });
      
      if (response.success && response.data) {
        setTeachers(response.data);
      } else {
        message.error(response.message || 'Müəllimlər yüklənmədi');
      }
    } catch (error) {
      console.error('Müəllimlər yüklənmədi:', error);
      message.error('Müəllimlər yüklənmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTeacher(null);
    setIsModalVisible(true);
  };

  const handleEdit = (teacher: TeacherList) => {
    setEditingTeacher(teacher);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number, teacherName: string) => {
    try {
      const response = await teacherAPI.deleteTeacher(id);
      if (response.success) {
        message.success(response.message || 'Müəllim silindi');
        loadTeachers();
      } else {
        message.error(response.message || 'Müəllim silinmədi');
      }
    } catch (error) {
      console.error('Müəllim silinmədi:', error);
      message.error('Müəllim silinmədi');
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingTeacher(null);
  };

  const handleFormSuccess = () => {
    setIsModalVisible(false);
    setEditingTeacher(null);
    loadTeachers();
  };

  const columns = [
    {
      title: 'Müəllim',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text: string, record: TeacherList) => (
        <div className="flex items-center space-x-3">
          <Avatar size="large" className="bg-blue-500">
            {text.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div className="font-medium text-gray-900">{text}</div>
            <div className="text-sm text-gray-500">FIN: {record.fin_code}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Əlaqə',
      key: 'contact',
      render: (record: TeacherList) => (
        <div className="space-y-1">
          {record.phone && (
            <div className="flex items-center text-sm text-gray-600">
              <PhoneOutlined className="mr-1" />
              {record.phone}
            </div>
          )}
          {record.email && (
            <div className="flex items-center text-sm text-gray-600">
              <MailOutlined className="mr-1" />
              {record.email}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'İş məlumatları',
      key: 'work_info',
      render: (record: TeacherList) => (
        <div className="space-y-1">
          <div className="flex items-center">
            <Tag color="blue">{record.work_percentage}%</Tag>
            <span className="text-sm text-gray-600 ml-2">
              {workPercentageLabels[record.work_percentage as keyof typeof workPercentageLabels] || `${record.work_percentage}%`}
            </span>
          </div>
          <div className="text-sm font-medium text-green-600">
            {record.salary.toLocaleString()} AZN
          </div>
        </div>
      ),
    },
    {
      title: 'Kurslar',
      key: 'courses',
      render: (record: TeacherList) => (
        <div>
          <div className="flex items-center mb-1">
            <BookOutlined className="mr-1 text-gray-500" />
            <span className="text-sm font-medium">{record.course_count} kurs</span>
          </div>
          <div className="space-y-1">
            {record.courses_names.map((courseName, index) => (
              <Tag key={index} color="green" className="text-xs">
                {courseName}
              </Tag>
            ))}
            {record.course_count > 3 && (
              <Tag color="default" className="text-xs">
                +{record.course_count - 3} daha
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktiv' : 'Deaktiv'}
        </Tag>
      ),
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      render: (record: TeacherList) => (
        <Space>
          <Tooltip title="Redaktə et">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Müəllimi silmək"
            description={`${record.full_name} müəllimini silmək istədiyinizə əminsiniz?`}
            onConfirm={() => handleDelete(record.id, record.full_name)}
            okText="Bəli"
            cancelText="Xeyr"
            okType="danger"
          >
            <Tooltip title="Sil">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <UserOutlined className="text-6xl text-gray-300 mb-4" />
        <h3 className="text-xl text-gray-500 mb-2">Şirkət seçin</h3>
        <p className="text-gray-400">
          Müəllimləri görmək üçün yuxarıdan şirkət seçin.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <UserOutlined className="mr-3 text-blue-500" />
              Müəllimlər
            </h2>
            <p className="text-gray-600 mt-1">
              {selectedCompany.name} şirkətinin müəllimləri
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
            className="shadow-sm"
          >
            Yeni Müəllim
          </Button>
        </div>

        {/* Statistika kartları */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={8}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{teachers.length}</div>
              <div className="text-gray-600">Ümumi Müəllim</div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-green-600">
                {teachers.filter(t => t.is_active).length}
              </div>
              <div className="text-gray-600">Aktiv Müəllim</div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="text-center shadow-sm">
              <div className="text-2xl font-bold text-orange-600">
                {teachers.reduce((sum, t) => sum + t.course_count, 0)}
              </div>
              <div className="text-gray-600">Ümumi Kurs</div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Filtrlər */}
      <Card className="mb-6 shadow-sm">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Search
              placeholder="Müəllim axtar..."
              allowClear
              onSearch={(value) => setFilters({ ...filters, search: value })}
              className="w-full"
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="İşləmə faizi"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters({ ...filters, work_percentage: value })}
              options={[
                { value: 25, label: '25% (Part-time)' },
                { value: 50, label: '50% (Half-time)' },
                { value: 75, label: '75% (3/4 time)' },
                { value: 100, label: '100% (Full-time)' }
              ]}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => setFilters({ ...filters, is_active: value })}
              options={[
                { value: true, label: 'Aktiv' },
                { value: false, label: 'Deaktiv' }
              ]}
            />
          </Col>
          <Col xs={24} sm={24} md={6} className="text-right">
            <Button
              onClick={() => setFilters({})}
              className="w-full md:w-auto"
            >
              Filtri Təmizlə
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Cədvəl */}
      <Card className="shadow-sm">
        <Table
          columns={columns}
          dataSource={teachers}
          rowKey="id"
          loading={loading}
          pagination={{
            total: teachers.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total} müəllim`,
          }}
          scroll={{ x: 1000 }}
          className="ant-table-striped"
          rowClassName={(record, index) => 
            index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
          }
        />
      </Card>

      {/* Modal */}
      {isModalVisible && (
        <TeacherForm
          visible={isModalVisible}
          teacher={editingTeacher}
          onCancel={handleModalClose}
          onSuccess={handleFormSuccess}
          selectedCompany={selectedCompany}
        />
      )}
    </div>
  );
};

export default TeachersList;